import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Modal, Form, Spinner, Badge, Alert } from 'react-bootstrap';
import { Plus, Edit, Trash2, Phone, Mail, MessageSquare } from 'lucide-react';
import Sidebar from '../components/Layout/Sidebar';
import api from '../services/api';
import './ContactsPage.css';
const ContactsPage = () => {
  const [contacts, setContacts] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [permissions, setPermissions] = useState({ canView: true, canInsert: false, canEdit: false, canDelete: false });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentContact, setCurrentContact] = useState({
    id: null, contact_type_id: 0, full_name: '', job_t: '', tel_m: '', email: '', chat: '', note: ''
  });
  useEffect(() => {
    loadContacts();
    loadContactTypes();
  }, []);
  const loadContacts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contacts');
const data = response.data.contacts ? response.data : { contacts: response.data, permissions: { canView: true, canInsert: false, canEdit: false, canDelete: false } };
setContacts(data.contacts || []);
setPermissions(data.permissions || { canView: true, canInsert: false, canEdit: false, canDelete: false });
    } catch (error) {
      console.error('Error loading contacts:', error);
      alert('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };
  const loadContactTypes = async () => {
    try {
      const response = await api.get('/contacts/types');
      setContactTypes(response.data);
    } catch (error) {
      console.error('Error loading contact types:', error);
    }
  };
  const handleSave = async () => {
    try {
      if (editMode) {
        await api.put(`/contacts/${currentContact.id}`, currentContact);
      } else {
        await api.post('/contacts', currentContact);
      }
      loadContacts();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving contact:', error);
      alert(error.response?.data?.message || 'Failed to save contact');
    }
  };
  const handleDelete = async (id) => {
    if (window.confirm('Delete this contact?')) {
      try {
        await api.delete(`/contacts/${id}`);
        loadContacts();
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert(error.response?.data?.message || 'Failed to delete contact');
      }
    }
  };
  const resetForm = () => {
    setCurrentContact({
      id: null, contact_type_id: 0, full_name: '', job_t: '', tel_m: '', email: '', chat: '', note: ''
    });
    setEditMode(false);
  };
  const handleEdit = (contact) => {
    setCurrentContact(contact);
    setEditMode(true);
    setShowModal(true);
  };
  return (
    <div className="d-flex">
      <Sidebar />
      <Container fluid className="contacts-page p-4">
        <div className="page-header">
          <div>
            <h2 className="page-title">Contacts Management</h2>
            <p className="page-subtitle">Manage your contact information</p>
          </div>
          {permissions.canInsert && (
            <Button variant="primary" onClick={() => { resetForm(); setShowModal(true); }} className="add-button">
              <Plus size={18} className="me-2" />
              Add Contact
            </Button>
          )}
        </div>
        {!permissions.canView && (
          <Alert variant="warning">You don't have permission to view contacts</Alert>
        )}
        {permissions.canView && loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Loading contacts...</p>
          </div>
        ) : permissions.canView && contacts.length === 0 ? (
          <Alert variant="info">No contacts found. {permissions.canInsert && 'Click "Add Contact" to create your first contact.'}</Alert>
        ) : permissions.canView && (
          <div className="table-container">
            <Table hover responsive className="contacts-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Job Title</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Chat</th>
                  <th>Type</th>
                  <th>Added</th>
                  {(permissions.canEdit || permissions.canDelete) && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr key={contact.id}>
                    <td><strong>{contact.full_name}</strong></td>
                    <td>{contact.job_t || '-'}</td>
                    <td>
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="contact-link">
                          <Mail size={14} className="me-1" />
                          {contact.email}
                        </a>
                      ) : '-'}
                    </td>
                    <td>
                      {contact.tel_m ? (
                        <a href={`tel:${contact.tel_m}`} className="contact-link">
                          <Phone size={14} className="me-1" />
                          {contact.tel_m}
                        </a>
                      ) : '-'}
                    </td>
                    <td>{contact.chat || '-'}</td>
                    <td><Badge bg="info">{contact.type_name || 'General'}</Badge></td>
                    <td>{new Date(contact.add_date).toLocaleDateString()}</td>
                    {(permissions.canEdit || permissions.canDelete) && (
                      <td>
                        <div className="action-buttons">
                          {permissions.canEdit && (
                            <Button size="sm" variant="outline-primary" onClick={() => handleEdit(contact)} className="me-1">
                              <Edit size={14} />
                            </Button>
                          )}
                          {permissions.canDelete && (
                            <Button size="sm" variant="outline-danger" onClick={() => handleDelete(contact.id)}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Contact' : 'Add New Contact'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Full Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter full name"
                  value={currentContact.full_name}
                  onChange={(e) => setCurrentContact({ ...currentContact, full_name: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Job Title</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Job title or position"
                  value={currentContact.job_t}
                  onChange={(e) => setCurrentContact({ ...currentContact, job_t: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="email@example.com"
                  value={currentContact.email}
                  onChange={(e) => setCurrentContact({ ...currentContact, email: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="+1234567890"
                  value={currentContact.tel_m}
                  onChange={(e) => setCurrentContact({ ...currentContact, tel_m: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Chat (WhatsApp/Skype was good/Telegram)</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="@username or Skype ID"
                  value={currentContact.chat}
                  onChange={(e) => setCurrentContact({ ...currentContact, chat: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select
                  value={currentContact.contact_type_id}
                  onChange={(e) => setCurrentContact({ ...currentContact, contact_type_id: parseInt(e.target.value) })}
                >
                  <option value="0">Select Type</option>
                  {contactTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.type_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Note</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Additional notes..."
                  value={currentContact.note}
                  onChange={(e) => setCurrentContact({ ...currentContact, note: e.target.value })}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave}>{editMode ? 'Update' : 'Create'}</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};
export default ContactsPage;